import React, { useCallback, useRef, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { Container, NavigationScroll, useDialogHelpers } from '@uncinc/uncinc-react-kitchen-sink';
import { RecursiveMenuTransitions } from '@uncinc/react-drupal-core';

import { SEARCHKIT_HITS_PER_PAGE_ALL_CONTENT_AUTOSUGGEST } from '../../config/constants';
import MainMenu from '../../implementations/Menu/MainMenu';
import NavbarMenu from '../../implementations/Menu/NavbarMenu';
import {
  Button,
  SearchAllContent,
  SiteLogo,
} from '../../components';

import './style.scss';

interface HeaderProps {
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

interface HeaderInnerProps {
  searchInputRef?: React.RefObject<HTMLInputElement>;
  isOverlayOpen: boolean;
  toggleOverlay: (onlyClose?: boolean) => void;
}

const HeaderInner: React.FC<HeaderInnerProps> = ({
  searchInputRef,
  isOverlayOpen,
  toggleOverlay,
}) => {
  const { t } = useTranslation();
  const collapseAll = RecursiveMenuTransitions.useCollapseSubmenus();
  const currentItem = RecursiveMenuTransitions.useExpandedSubmenu();

  const overlayClasses = c('SiteHeader__overlay', {
    'SiteHeader__overlay--visible': currentItem || isOverlayOpen,
  });

  return (
    <div className="SiteHeader">
      <Container>
        <div className="SiteHeader__inner">
          <SiteLogo />

          <Button
            className="SiteHeader__hamburger"
            type="secondary"
            icon={isOverlayOpen ? 'clear' : 'menu'}
            iconPosition="left"
            aria-expanded={isOverlayOpen}
            onClick={() => {
              toggleOverlay();
            }}
          >
            {t(`buttons.${isOverlayOpen ? 'close' : 'menu'}`)}
          </Button>

          <div className="SiteHeader__menus">
            <MainMenu
              onClickFunction={() => {
                toggleOverlay(true);
                collapseAll();
              }}
            />

            <NavbarMenu
              onClickFunction={() => {
                toggleOverlay(true);
                collapseAll();
              }}
            />
          </div>

          <SearchAllContent className="SiteHeader__searchbar" hitsPerPage={SEARCHKIT_HITS_PER_PAGE_ALL_CONTENT_AUTOSUGGEST} onlyFetchWithQuery>
            <SearchAllContent.SearchBarAutosuggest type="secondary" searchInputRef={searchInputRef} searchbarProps={{ fieldName: 'search-header' }} />
          </SearchAllContent>
        </div>
      </Container>

      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={overlayClasses}
        onClick={() => collapseAll()}
      />
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ searchInputRef }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const isOverlayOpen = useRef<boolean>(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { t } = useTranslation();

  const toggleOverlay = useCallback((onlyClose = false) => {
    if (onlyClose) {
      isOverlayOpen.current = false;
    } else {
      isOverlayOpen.current = !isOverlayOpen.current;
    }

    setDialogOpen(isOverlayOpen.current);
  }, []);

  const { dialogHelpersProps } = useDialogHelpers<HTMLDivElement>({
    wrapperElement: headerRef,
    isOpen: isDialogOpen,
    closeFunction: toggleOverlay,
    disableClosers: false,
    ariaLabel: t('nav.main') ?? '',
  });

  return (
    <header
      className="SiteHeader__wrapper"
    >
      <NavigationScroll>
        <div
          ref={headerRef}
          {...dialogHelpersProps}
        >
          <RecursiveMenuTransitions.MenuTransitionProvider>
            <HeaderInner
              searchInputRef={searchInputRef}
              isOverlayOpen={isOverlayOpen.current}
              toggleOverlay={toggleOverlay}
            />
          </RecursiveMenuTransitions.MenuTransitionProvider>
        </div>
      </NavigationScroll>
    </header>
  );
};

export default Header;
